// Stadium Trial — interactive turn-based combat engine (A/B variant B "spells").
//
// Pure state machine, ZERO persistence and ZERO React: the UI drives it with
// player actions (auto attack / cast spell / end turn) and renders the state
// after each step. Nothing is written mid-fight (exactly like simulateCombat) —
// a refresh means the fight never happened.
//
// simulateCombat (pveEngine) stays UNTOUCHED: it is the control variant's
// source of truth. This engine shares its pure helpers (computeHit,
// enemyStatsFor, critChance, defenseReductionFor, maxHpFromStats) so both
// variants fight the same enemies with the same base math, and it produces the
// SAME CombatSequence contract (frames + BossResult) — everything downstream
// (savePveStageWin, telemetry, result screens) works unchanged.
//
// Turn loop:
//   1. Player turn starts with a FULL bar: ENERGY_PER_TURN energy, every turn.
//      Nothing carries over — unspent energy is simply lost (no banking, no
//      recharge bookkeeping). There is NO auto attack: spells are the only
//      player actions.
//   2. Cast freely while energy lasts (cooldowns are the real limiter).
//   3. endTurn() → the enemy acts once, then the next player turn starts
//      (bar refilled). The turn auto-ends when no spell is castable — leftover
//      energy has no value.
//
// Variant-B stat roles (this engine only — control keeps the classic roles):
//   - Puissance  → spell damage.
//   - Défense    → damage reduction (shared formula) + improves shields
//                  (shieldScalesWithDefense) + reduces CRIT damage received
//                  (the ×2 crit bonus is shrunk by the defense reduction).
//   - Technicité → crit chance (shared formula). Energy-drain resistance is
//                  declared in design but NOT consumed yet: no enemy drains
//                  energy in v1 (future boss effect).
//   - Vie        → max HP (shared maxHpFromStats).
//
// No boosts in variant B: pve_active_bonuses stays [] and no CombatModifiers
// are resolved here.

import { calculateMatchAdvantage } from "../../league-duel/engine/combat";
import type { EquipmentStats } from "../../league-duel/engine/combat";
import { getEncounter, DEFAULT_TIER } from "../content/pveTiers";
import { getSpell } from "../content/pveSpells";
import type { PveSpellId } from "../content/pveSpells";
import {
  ENEMY_PET_DAMAGE_PERCENT,
  PET_BOOST_PERCENT,
  PET_ENERGY_PER_TURN,
  PET_HEAL_PERCENT_MASTER_DEF,
  PET_SPELL_POOL,
  PET_STRIKE_DAMAGE_PERCENT,
  getPetSpell,
  petMaxHpFor,
} from "../content/pvePets";
import type { PetSpellId } from "../content/pvePets";
import {
  BossResult,
  CombatFrame,
  CombatSequence,
  CRIT_MULTIPLIER,
  MAX_CONDITION,
  PVE_HP_PER_VITESSE,
  computeHit,
  critChance,
  defenseReductionFor,
  enemyStatsFor,
  getStageRewards,
  maxHpFromStats,
} from "./pveEngine";

// ── Energy system ─────────────────────────────────────────────────────────────
// A FULL bar every player turn, nothing carries over, no auto attack.
export const ENERGY_MAX = 6;
export const ENERGY_PER_TURN = 6;
/** Safety cap on player turns (control uses 60 half-turns ≈ 30 rounds). */
export const MAX_ROUNDS = 30;

// ── Rotation mode (Ulala test) ────────────────────────────────────────────────
// The fight plays ITSELF. Every player turn: one auto attack (+1 energy,
// banked across turns, cap ENERGY_MAX), then the NEXT spell of the rotation
// fires if affordable — strict order, no skipping (a head on cooldown or too
// expensive means "keep auto-attacking until it comes up", so swapping one
// spell shifts the whole rotation's timing, the Ulala hook).
// Rotation order = the loadout order confirmed at spell-select (pick order,
// Classic Strike first as the mandatory pre-pick).
// The player's skill moves to BEFORE the fight: build the right rotation.
export const PVE_ROTATION_ENABLED = true;
/** GAUGE SCALE — rotation costs/income are catalogue units ×10, so tempo
 *  bonuses stay integers (+11, +12/auto instead of +1.1): a cost-3 spell
 *  reads 30 on the gauge. Catalogue costs and the full-bar variant are
 *  untouched — this is a display/granularity scale, not a rebalance. */
export const ROTATION_GAUGE_SCALE = 10;
/** Gauge cap — 6 catalogue units, i.e. 60 gauge points (banked, carries over). */
export const ROTATION_ENERGY_MAX = ENERGY_MAX * ROTATION_GAUGE_SCALE;
/** Energy per auto attack, in GAUGE points. Base 10 = 1 catalogue unit.
 *  Item energy affixes ADD to this (each affix unit = +1 point = +10%):
 *  a +3 ball reads "Energy gain +13 (+30%)". Stored per fight in
 *  state.energyPerAuto. */
export const ROTATION_ENERGY_PER_AUTO = 10;
/** Auto-attack damage as % of a base hit — the rotation-mode DPS floor.
 *  Ulala feel (weak autos, spells are the payoff). Calibrated by simulation
 *  (50 fights/stage vs the full-bar reference): 60 + spells ×3 matches its
 *  win rates; 20/×1 and 40/×2 both dragged into timeouts. */
export const ROTATION_AUTO_DAMAGE_PERCENT = 60;
/** Rotation-only spell damage multiplier — every damagePercent hits ×3 in
 *  rotation (catalogue & full-bar variant untouched, A/B comparability
 *  preserved). Spells stay the payoff: Classic Strike 195% vs auto 60%. */
export const ROTATION_SPELL_DAMAGE_MULT = 3;

/** A spell's cost in GAUGE points (rotation mode) — catalogue cost ×10. */
export function rotationSpellCost(id: PveSpellId): number {
  return (getSpell(id)?.cost ?? 0) * ROTATION_GAUGE_SCALE;
}

// ── Enemy energy & kits (variant B) ───────────────────────────────────────────
// Enemies play their energy like the player: a FULL bar at each of their turns,
// spent on a scripted kit (fully predictable — no telegraph needed):
//
//   MOB (stages 1-4, 6-9): 3⚡/turn. Strike at 60% of its damage (2⚡) EVERY
//   turn, plus a heal of 5% of its own max HP (1⚡, cooldown 2) every 2 turns.
//   No rage.
//
//   BOSS (stages 5 & 10): 6⚡/turn. DOUBLE strike — two hits of 60% each (3⚡
//   per hit — the full bar), every turn. Plus the RAGE passive: +5% damage per
//   20% of its own max HP missing (at 20% HP left → +20%) — the variant-B
//   replacement for the control-only boss_berserker_scaling effect.
export const MOB_ENERGY_PER_TURN = 3;
export const MOB_ATTACK_COST = 2;
export const MOB_ATTACK_DAMAGE_PERCENT = 60;
export const MOB_HEAL_COST = 1;
export const MOB_HEAL_PERCENT_MAX_HP = 5;
export const MOB_HEAL_COOLDOWN = 2;             // player-visible rhythm: heals every 2 turns
export const BOSS_ENERGY_PER_TURN = 6;
export const BOSS_STRIKE_COST = 3;   // ×2 strikes = the whole 6⚡ bar
export const BOSS_STRIKE_DAMAGE_PERCENT = 60;
export const BOSS_STRIKES_PER_TURN = 2;
export const ENEMY_RAGE_STEP_PERCENT = 20;      // per 20% of max HP missing…
export const ENEMY_RAGE_DAMAGE_PER_STEP = 5;    // …+5% damage (BOSS ONLY)
/** Frame marker for the mob heal (UI cue + telemetry). */
export const ENEMY_HEAL_ID = "enemy_heal";

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

// ── State ─────────────────────────────────────────────────────────────────────

export interface InteractiveCombatState {
  tier: number;
  stage: number;
  /** 1-based player turn counter (a round = player turn + enemy reply). */
  round: number;
  over: boolean;
  win: boolean | null;          // set when over
  energy: number;
  /** Energy granted at the start of every player turn (6 base + item affix bonus). */
  energyPerTurn: number;
  playerHp: number;
  playerMaxHp: number;
  playerShield: number;         // absorbs before HP, persists across turns, never persisted
  enemyHp: number;
  enemyMaxHp: number;
  enemyKind: "mob" | "boss";    // picks the enemy kit (bar size, script, rage)
  /** VISIBLE enemy gauge — full bar at ITS turn (mob 3 / boss 6), shows leftovers. */
  enemyEnergy: number;
  enemyEnergyMax: number;       // 3 (mob) or 6 (boss) — for the UI pips
  /** Mob heal cooldown (remaining enemy turns) — heals every 2 turns. */
  enemyHealCooldown: number;
  loadout: PveSpellId[];
  /** Same frame log as simulateCombat — feeds telemetry + the final sequence. */
  frames: CombatFrame[];
  // ── Buffs / debuffs / cooldowns (in-fight only, never persisted) ──
  /** Focus: damage bonus (%) on ALL offensive spells for the CURRENT turn.
   * Cleared at turn end (not consumed per cast). */
  focusBonusPercent: number;
  /** Feint: pending FINAL-damage bonus (%) on the player's next offensive spell. */
  offBalancePercent: number;
  /** Vision echo mark: % of a base hit per energy the enemy spends on ITS next
   * turn, dealt at the end of that turn. 0 = no mark. One turn only. */
  visionEchoPerEnergy: number;
  /** Per-spell cooldowns (remaining player turns). Ticked at each turn start. */
  cooldowns: Partial<Record<PveSpellId, number>>;
  /** Once-per-turn spells already cast this turn. Reset at turn start. */
  oncePerTurnCast: Partial<Record<PveSpellId, boolean>>;
  /** Wall: the shield expires at the START of this player turn (null = no timer). */
  shieldExpiresRound: number | null;
  /** Wall: shield gained per enemy CRIT received while the wall is active. */
  wallCritGain: number;
  // Frozen at creation (the fight config).
  playerStats: EquipmentStats;
  enemyStats: EquipmentStats;
  enemyDamageMult: number;
  startHp: number;
  playerLevel: number;          // Wall scales on the game level (level × shieldPerLevel)
  // ── Rotation mode (Ulala test) ──
  /** true = the fight plays itself (auto attack + rotation), no manual casts. */
  rotationMode: boolean;
  /** Gauge points gained per auto attack — base 10 + item energy affixes. */
  energyPerAuto: number;
  /** Next rotation slot to fire (index into loadout, wraps around). */
  rotationIndex: number;
  /** Auto attacks performed (telemetry). */
  autoAttacks: number;
  // Telemetry counters (variant_stats).
  spellsCast: Partial<Record<PveSpellId, number>>;
  energySpent: number;
  enemySpellsCast: number;      // boss spells fired + mob shields raised
  // ── 2v2 pets test (PVE_PETS_ENABLED) ──
  /** Pets in this fight? false = the classic 1v1 flow, untouched. */
  petsEnabled: boolean;
  /** Whose ALLY action bar is live. Interlaced order per round:
   * player → enemy master → ally pet → enemy pet. */
  phase: "player" | "pet";
  petHp: number;
  petMaxHp: number;             // 0 when petsEnabled is false
  /** Pet energy — full bar (PET_ENERGY_PER_TURN) at each pet phase. */
  petEnergy: number;
  /** Per-pet-spell cooldowns (remaining pet turns, ticked at pet phase start). */
  petCooldowns: Partial<Record<PetSpellId, number>>;
  /** Battle Cry: +X% on the master's NEXT offensive spell (consumed, no stacking). */
  masterBoostPercent: number;
  enemyPetHp: number;
  enemyPetMaxHp: number;
  // Pet telemetry (variant_stats).
  petSpellsCast: Partial<Record<PetSpellId, number>>;
  petEnergySpent: number;
}

/** Rage passive — BOSS ONLY: ×(1 + 5% per full 20% of its own max HP missing). */
export function enemyRageMultiplier(s: Pick<InteractiveCombatState, "enemyHp" | "enemyMaxHp" | "enemyKind">): number {
  if (s.enemyKind !== "boss") return 1;
  const missing = Math.max(0, Math.min(1, 1 - s.enemyHp / s.enemyMaxHp));
  return 1 + Math.floor(missing / (ENEMY_RAGE_STEP_PERCENT / 100)) * (ENEMY_RAGE_DAMAGE_PER_STEP / 100);
}

// ── Creation ──────────────────────────────────────────────────────────────────

/**
 * Build the fight from the SAME inputs as simulateCombat: condition (%) is
 * converted to real HP on entry (and back to % by finalize). Turn 1 opens
 * with a full energy bar, like every turn.
 */
export function createInteractiveCombat(
  stage: number,
  playerStats: EquipmentStats,
  loadout: PveSpellId[],
  currentCondition: number,
  tier: number = DEFAULT_TIER,
  playerLevel: number = 1,
  energyBonus: number = 0,
  petsEnabled: boolean = false,
  rotationMode: boolean = false
): InteractiveCombatState {
  const encounter = getEncounter(tier, stage);
  const enemyStats = enemyStatsFor(encounter);
  const enemyKind: "mob" | "boss" = encounter?.kind === "boss" ? "boss" : "mob";
  const playerMaxHp = Math.max(1, maxHpFromStats(playerStats.vitesse));
  const enemyMaxHp = Math.max(1, Math.round(enemyStats.vitesse * PVE_HP_PER_VITESSE));
  const startCond = clamp(currentCondition, 0, MAX_CONDITION);
  const startHp = (startCond / MAX_CONDITION) * playerMaxHp;
  // Energy/turn = base 6 + item affix bonus (ball). The full bar refills to this.
  const energyPerTurn = ENERGY_PER_TURN + Math.max(0, energyBonus);
  // Pets — HP derives from each master (fresh every fight, nothing persisted).
  const petMaxHp = petsEnabled ? petMaxHpFor(playerMaxHp, playerStats) : 0;
  const enemyPetMaxHp = petsEnabled ? petMaxHpFor(enemyMaxHp, enemyStats) : 0;
  // A master dead on entry (condition 0 mid-run) hands the fight to its pet.
  const startPhase: "player" | "pet" = petsEnabled && startHp <= 0 ? "pet" : "player";

  return {
    tier,
    stage,
    round: 1,
    over: false,
    win: null,
    // Rotation: energy is BANKED gauge points, starts empty — the ball affix
    // boosts the GAIN PER AUTO instead (see energyPerAuto). Full-bar otherwise.
    energy: rotationMode ? 0 : energyPerTurn,
    energyPerTurn,
    playerHp: startHp,
    playerMaxHp,
    playerShield: 0,
    enemyHp: enemyMaxHp,
    enemyMaxHp,
    enemyKind,
    enemyEnergy: enemyKind === "boss" ? BOSS_ENERGY_PER_TURN : MOB_ENERGY_PER_TURN,
    enemyEnergyMax: enemyKind === "boss" ? BOSS_ENERGY_PER_TURN : MOB_ENERGY_PER_TURN,
    enemyHealCooldown: 0,
    loadout: [...loadout],
    frames: [],
    focusBonusPercent: 0,
    offBalancePercent: 0,
    visionEchoPerEnergy: 0,
    cooldowns: {},
    oncePerTurnCast: {},
    shieldExpiresRound: null,
    wallCritGain: 0,
    playerStats,
    enemyStats,
    enemyDamageMult: encounter?.damageMultiplier ?? 1,
    startHp,
    playerLevel: Math.max(1, playerLevel),
    rotationMode,
    energyPerAuto: ROTATION_ENERGY_PER_AUTO + Math.max(0, energyBonus),
    rotationIndex: 0,
    autoAttacks: 0,
    spellsCast: {},
    energySpent: 0,
    enemySpellsCast: 0,
    petsEnabled,
    phase: startPhase,
    petHp: petMaxHp,
    petMaxHp,
    petEnergy: startPhase === "pet" ? PET_ENERGY_PER_TURN : 0,
    petCooldowns: {},
    masterBoostPercent: 0,
    enemyPetHp: enemyPetMaxHp,
    enemyPetMaxHp,
    petSpellsCast: {},
    petEnergySpent: 0,
  };
}

// ── Affordability helpers (UI enable/disable + auto-end rule) ─────────────────

export function canCastSpell(s: InteractiveCombatState, id: PveSpellId): boolean {
  if (s.over || !s.loadout.includes(id)) return false;
  if (s.petsEnabled && (s.phase !== "player" || s.playerHp <= 0)) return false;
  const def = getSpell(id);
  if (!def) return false;
  // Rotation gauge: costs live in gauge points (catalogue ×10).
  const cost = s.rotationMode ? def.cost * ROTATION_GAUGE_SCALE : def.cost;
  if (s.energy < cost) return false;
  if ((s.cooldowns[id] ?? 0) > 0) return false;
  if (def.effects.oncePerTurn && s.oncePerTurnCast[id]) return false;
  // Reckless Strike guard: an HP-cost spell is uncastable when it would kill
  // the caster (a self-inflicted run reset is design-breaking, not "risky").
  const hpCost = def.effects.selfDamagePercentMaxHp;
  if (hpCost != null && s.playerHp <= (hpCost / 100) * s.playerMaxHp) return false;
  return true;
}

/** Pet spell affordability — pet phase only, pet alive, energy + cooldown. */
export function canCastPetSpell(s: InteractiveCombatState, id: PetSpellId): boolean {
  if (s.over || !s.petsEnabled || s.phase !== "pet" || s.petHp <= 0) return false;
  const def = getPetSpell(id);
  if (!def || s.petEnergy < def.cost) return false;
  if ((s.petCooldowns[id] ?? 0) > 0) return false;
  return true;
}

/** True when NO action remains this phase (no castable spell for the active ally). */
export function noActionLeft(s: InteractiveCombatState): boolean {
  if (s.over) return true;
  if (s.petsEnabled && s.phase === "pet") return !PET_SPELL_POOL.some((id) => canCastPetSpell(s, id));
  return !s.loadout.some((id) => canCastSpell(s, id));
}

/**
 * Auto-validate: the bar refills every turn and leftovers are worthless, so
 * the turn self-ends as soon as nothing is castable (energy too low for every
 * spell, or everything on cooldown).
 */
export function shouldAutoEndTurn(s: InteractiveCombatState): boolean {
  // Rotation mode has its own driver (auto attack → maybe cast → end turn):
  // the "nothing castable" rule doesn't apply, energy banking is the point.
  if (s.rotationMode) return false;
  return !s.over && noActionLeft(s);
}

// ── Internals ─────────────────────────────────────────────────────────────────

const cloneState = (s: InteractiveCombatState): InteractiveCombatState => ({
  ...s,
  loadout: s.loadout,          // immutable after creation
  frames: [...s.frames],
  spellsCast: { ...s.spellsCast },
  cooldowns: { ...s.cooldowns },
  oncePerTurnCast: { ...s.oncePerTurnCast },
  petCooldowns: { ...s.petCooldowns },
  petSpellsCast: { ...s.petSpellsCast },
});

/** Pet HP snapshot appended to every frame while pets are on (UI bars + telemetry). */
const petFrameFields = (s: InteractiveCombatState) =>
  s.petsEnabled ? { petHp: Math.round(s.petHp), enemyPetHp: Math.round(s.enemyPetHp) } : {};

function pushPlayerFrame(s: InteractiveCombatState, damage: number, crit: boolean, heal?: number, spell?: string, selfDamage?: number, targetPet?: boolean) {
  s.frames.push({
    turn: s.round * 2 - 1, // odd = player, even = enemy (control-frame convention)
    attacker: "player",
    damage: Math.round(damage),
    crit,
    playerHp: Math.round(s.playerHp),
    bossHp: Math.round(s.enemyHp),
    playerShield: Math.round(s.playerShield),
    heal: heal && heal > 0 ? Math.round(heal) : undefined,
    spell,
    selfDamage: selfDamage && selfDamage > 0 ? Math.round(selfDamage) : undefined,
    targetPet: targetPet || undefined,
    ...petFrameFields(s),
  });
}

function checkEnemyDown(s: InteractiveCombatState) {
  if (s.enemyHp <= 0 && (!s.petsEnabled || s.enemyPetHp <= 0)) {
    s.over = true;
    s.win = true;
  }
}

/** The ally side still stands (a dead master doesn't end the fight in 2v2). */
const allySideAlive = (s: InteractiveCombatState): boolean =>
  s.playerHp > 0 || (s.petsEnabled && s.petHp > 0);

/**
 * Ally damage lands on the enemy MASTER while it stands, then retargets its
 * pet. Returns true when the pet took the hit (UI popup side note).
 */
function damageEnemy(s: InteractiveCombatState, damage: number): boolean {
  if (s.enemyHp > 0 || !s.petsEnabled) {
    s.enemyHp = Math.max(0, s.enemyHp - damage);
    return false;
  }
  s.enemyPetHp = Math.max(0, s.enemyPetHp - damage);
  return true;
}

// ── Player actions ────────────────────────────────────────────────────────────
// Spells are the ONLY player actions (the auto attack was removed with the
// full-bar energy model).

/** Cast one of the loadout spells (costs its catalogue energy + cooldown/HP cost). */
export function castSpell(prev: InteractiveCombatState, id: PveSpellId, rng: () => number = Math.random): InteractiveCombatState {
  if (!canCastSpell(prev, id)) return prev;
  const def = getSpell(id)!;
  const e = def.effects;
  const s = cloneState(prev);
  // Rotation gauge: consume the EXACT scaled cost — any overshoot from the
  // last auto attack stays banked (a +11/auto tempo build keeps its surplus).
  const cost = s.rotationMode ? def.cost * ROTATION_GAUGE_SCALE : def.cost;
  s.energy -= cost;
  s.energySpent += cost;
  s.spellsCast[id] = (s.spellsCast[id] ?? 0) + 1;
  if (e.cooldownTurns != null) s.cooldowns[id] = e.cooldownTurns;
  if (e.oncePerTurn) s.oncePerTurnCast[id] = true;

  let damage = 0;
  let crit = false;
  let healed = 0;
  let selfDamage = 0;
  let hitPet = false;

  // Focus — arm the turn-wide buff (boosts EVERY offensive spell until turn end).
  if (e.turnDamageBonusPercent != null) {
    s.focusBonusPercent += e.turnDamageBonusPercent;
  }

  // Reckless Strike — HP cost paid up front (canCastSpell guarantees survival).
  if (e.selfDamagePercentMaxHp != null) {
    selfDamage = (e.selfDamagePercentMaxHp / 100) * s.playerMaxHp;
    s.playerHp = Math.max(0, s.playerHp - selfDamage);
  }

  if (e.damagePercent != null) {
    // Damage spells scale on the SAME base hit as autos (Puissance drives both).
    const hit = computeHit(s.playerStats, s.enemyStats, 1, rng);
    crit = hit.crit;
    let mult = e.damagePercent / 100;
    // Rotation "+100% pass" — spells hit double (see ROTATION_SPELL_DAMAGE_MULT).
    if (s.rotationMode) mult *= ROTATION_SPELL_DAMAGE_MULT;
    // Focus buff — applies to EVERY offensive spell this turn (not consumed).
    if (s.focusBonusPercent > 0) {
      mult *= 1 + s.focusBonusPercent / 100;
    }
    damage = hit.damage * mult;
    // Feint's Off Balance (armed by a PREVIOUS hit) — FINAL damage, then consumed.
    if (s.offBalancePercent > 0) {
      damage *= 1 + s.offBalancePercent / 100;
      s.offBalancePercent = 0;
    }
    // Pet's Battle Cry — +10% on the master's next attack, then consumed.
    if (s.masterBoostPercent > 0) {
      damage *= 1 + s.masterBoostPercent / 100;
      s.masterBoostPercent = 0;
    }
    hitPet = damageEnemy(s, damage);

    // Crit riders.
    if (crit) {
      if (e.critEnergyRefund != null) {
        const refund = s.rotationMode ? e.critEnergyRefund * ROTATION_GAUGE_SCALE : e.critEnergyRefund;
        const cap = s.rotationMode ? ROTATION_ENERGY_MAX : s.energyPerTurn;
        s.energy = Math.min(cap, s.energy + refund);
      }
      if (e.healOnCritPercentMaxHp != null) {
        healed = (e.healOnCritPercentMaxHp / 100) * s.playerMaxHp;
        s.playerHp = Math.min(s.playerMaxHp, s.playerHp + healed);
      }
    }
  }

  // Feint — arm Off Balance AFTER this spell's own hit (it boosts the NEXT one).
  if (e.applyOffBalancePercent != null) {
    s.offBalancePercent = e.applyOffBalancePercent; // overwrite, no stacking
  }

  // Vision — arm the ECHO mark: at the end of the enemy's next turn it takes
  // +X% of a base hit per energy IT SPENT that turn (resolved in endTurn).
  if (e.echoPerEnemyEnergyPercent != null) {
    s.visionEchoPerEnergy = e.echoPerEnemyEnergyPercent;
  }

  // Wall — timed flat shield that GROWS on enemy crits received. Recast replaces it.
  if (e.shieldFlat != null) {
    s.playerShield = e.shieldFlat;
    s.shieldExpiresRound = s.round + (e.shieldDurationTurns ?? 2);
    s.wallCritGain = e.shieldPerEnemyCrit ?? 0;
  }

  pushPlayerFrame(s, damage, crit, healed, id, selfDamage, hitPet);
  checkEnemyDown(s);
  return s;
}

// ── Rotation mode (Ulala test) — the whole player turn in one call ───────────

/**
 * Plays the player's ENTIRE turn automatically:
 *   1. Auto attack — ROTATION_AUTO_DAMAGE_PERCENT of a base hit,
 *      +ROTATION_ENERGY_PER_AUTO gauge points (banked, cap ROTATION_ENERGY_MAX).
 *   2. The rotation HEAD (loadout[rotationIndex]) fires if it is castable
 *      (energy, cooldown, HP guard). STRICT order: an unaffordable head is
 *      awaited, never skipped — that's what makes spell costs a timing tool.
 * The UI then calls endTurn() as usual (enemy reply unchanged).
 */
export function rotationPlayerAction(prev: InteractiveCombatState, rng: () => number = Math.random): InteractiveCombatState {
  if (prev.over || !prev.rotationMode) return prev;

  // 1. Auto attack — same base hit as spells (Puissance drives both).
  const s = cloneState(prev);
  const hit = computeHit(s.playerStats, s.enemyStats, 1, rng);
  const damage = hit.damage * (ROTATION_AUTO_DAMAGE_PERCENT / 100);
  const hitPet = damageEnemy(s, damage);
  s.autoAttacks += 1;
  s.energy = Math.min(ROTATION_ENERGY_MAX, s.energy + s.energyPerAuto);
  pushPlayerFrame(s, damage, hit.crit, undefined, undefined, undefined, hitPet);
  checkEnemyDown(s);
  if (s.over) return s;

  // 2. Rotation head — cast if possible, then advance (wrap after the last).
  if (s.loadout.length > 0) {
    const head = s.loadout[s.rotationIndex % s.loadout.length];
    const after = castSpell(s, head, rng);
    if (after !== s) {
      after.rotationIndex = s.rotationIndex + 1;
      return after;
    }
  }
  return s;
}

/** The spell the rotation will fire next (UI highlight). */
export function rotationHead(s: InteractiveCombatState): PveSpellId | null {
  if (!s.rotationMode || s.loadout.length === 0) return null;
  return s.loadout[s.rotationIndex % s.loadout.length];
}

/** Cast one of the pet's 3 spells (pet phase only — see canCastPetSpell). */
export function castPetSpell(prev: InteractiveCombatState, id: PetSpellId, rng: () => number = Math.random): InteractiveCombatState {
  if (!canCastPetSpell(prev, id)) return prev;
  const def = getPetSpell(id)!;
  const s = cloneState(prev);
  s.petEnergy -= def.cost;
  s.petEnergySpent += def.cost;
  s.petSpellsCast[id] = (s.petSpellsCast[id] ?? 0) + 1;
  if (def.cooldownTurns != null) s.petCooldowns[id] = def.cooldownTurns;

  let damage = 0;
  let crit = false;
  let healed = 0;
  let hitPet = false;

  // Battle Cry — arm the master's next-attack boost (overwrite, no stacking).
  if (id === "pet_battle_cry") {
    s.masterBoostPercent = PET_BOOST_PERCENT;
  }

  // Zone Heal — pet AND master each recover 15% of the master's defense.
  // No revive: a dead master stays down.
  if (id === "pet_zone_heal") {
    const amount = (PET_HEAL_PERCENT_MASTER_DEF / 100) * s.playerStats.defense;
    const petHealed = Math.min(amount, s.petMaxHp - s.petHp);
    s.petHp += petHealed;
    let masterHealed = 0;
    if (s.playerHp > 0) {
      masterHealed = Math.min(amount, s.playerMaxHp - s.playerHp);
      s.playerHp += masterHealed;
    }
    healed = petHealed + masterHealed;
  }

  // Pet Strike — 30% of a master base hit, single target (master then its pet).
  if (id === "pet_strike") {
    const hit = computeHit(s.playerStats, s.enemyStats, 1, rng);
    crit = hit.crit;
    damage = hit.damage * (PET_STRIKE_DAMAGE_PERCENT / 100);
    hitPet = damageEnemy(s, damage);
  }

  s.frames.push({
    turn: s.round * 2 - 1,
    attacker: "player",
    actor: "allyPet",
    damage: Math.round(damage),
    crit,
    playerHp: Math.round(s.playerHp),
    bossHp: Math.round(s.enemyHp),
    playerShield: Math.round(s.playerShield),
    heal: healed > 0 ? Math.round(healed) : undefined,
    spell: id,
    targetPet: hitPet || undefined,
    ...petFrameFields(s),
  });
  checkEnemyDown(s);
  return s;
}

/**
 * Validate / end of the ACTIVE ALLY's phase.
 *
 * 1v1 (pets off — unchanged): the enemy attacks once, then the next player
 * turn opens. 2v2 (pets on), interlaced order per round:
 *   player phase → enemy master → ALLY PET PHASE → enemy pet → next round.
 * A dead ally is skipped; a dead master hands its round slot to its pet (the
 * enemy master then acts between the pet phase and the enemy pet).
 * Past MAX_ROUNDS the fight resolves by remaining SIDE HP fraction
 * (player-favoured on exact ties — control rule).
 */
export function endTurn(prev: InteractiveCombatState, rng: () => number = Math.random): InteractiveCombatState {
  if (prev.over) return prev;
  const s = cloneState(prev);

  if (!s.petsEnabled) {
    enemyMasterActs(s, rng);
    if (s.over) return s;
    return nextRound(s);
  }

  if (s.phase === "player") {
    enemyMasterActs(s, rng);
    if (s.over) return s;
    if (s.petHp > 0) return enterPetPhase(s);
    enemyPetActs(s, rng);
    if (s.over) return s;
    return nextRound(s);
  }

  // Pet phase ends. A dead master means the round had no player phase — the
  // enemy master still owes its action (order: pet → enemy → enemy pet).
  if (s.playerHp <= 0) {
    enemyMasterActs(s, rng);
    if (s.over) return s;
  }
  enemyPetActs(s, rng);
  if (s.over) return s;
  return nextRound(s);
}

// ── Enemy master turn ── FULL bar (mob 3 / boss 6), then its scripted kit:
//   mob:  one 60% strike (2⚡) + a 5%-max-HP heal (1⚡, CD 2) every 2 turns;
//   boss: two 60% strikes (3⚡ each — the whole bar), rage passive applied.
// 2v2: strikes hit the player while they stand, then retarget the ally pet.
function enemyMasterActs(s: InteractiveCombatState, rng: () => number) {
  if (s.enemyHp <= 0) {
    // Dead master: no action, no telegraph, and the echo mark fizzles.
    s.enemyEnergy = 0;
    s.visionEchoPerEnergy = 0;
    return;
  }

  const isBoss = s.enemyKind === "boss";
  s.enemyEnergy = isBoss ? BOSS_ENERGY_PER_TURN : MOB_ENERGY_PER_TURN;
  if (s.enemyHealCooldown > 0) s.enemyHealCooldown -= 1;

  let enemyEnergySpent = 0;
  const rage = enemyRageMultiplier(s); // boss only; frozen for this turn's hits
  const reduction = defenseReductionFor(s.playerStats.defense); // pet shares the master's stats
  const strikes = isBoss ? BOSS_STRIKES_PER_TURN : 1;
  const strikeCost = isBoss ? BOSS_STRIKE_COST : MOB_ATTACK_COST;
  const strikePercent = isBoss ? BOSS_STRIKE_DAMAGE_PERCENT : MOB_ATTACK_DAMAGE_PERCENT;

  for (let i = 0; i < strikes && allySideAlive(s) && s.enemyEnergy >= strikeCost; i++) {
    s.enemyEnergy -= strikeCost;
    enemyEnergySpent += strikeCost;

    const base = s.enemyStats.physique * (0.90 + rng() * 0.20);
    const crit = rng() < critChance(s.enemyStats.technique, s.playerStats.technique);
    // Variant-B Défense: the +100% crit bonus is shrunk by the defense reduction
    // (crit mult ranges ×2 at 0 defense → ×1.5 at defense 200), THEN the normal
    // flat reduction applies to the whole hit.
    const critMult = crit ? CRIT_MULTIPLIER - reduction : 1;
    const raw = base * critMult * s.enemyDamageMult * rage * (strikePercent / 100);
    const damage = Math.max(1, raw * (1 - reduction));

    const hitPlayer = s.playerHp > 0;
    if (hitPlayer) {
      const absorbed = Math.min(s.playerShield, damage);
      s.playerShield -= absorbed;
      s.playerHp = Math.max(0, s.playerHp - (damage - absorbed));
      // Wall reactive gain — an enemy CRIT feeds the active wall (after the hit lands).
      if (crit && s.shieldExpiresRound != null && s.wallCritGain > 0) {
        s.playerShield += s.wallCritGain;
      }
    } else {
      // Master down → the strike lands on the ally pet (no shield, no wall).
      s.petHp = Math.max(0, s.petHp - damage);
    }

    s.frames.push({
      turn: s.round * 2,
      attacker: "boss",
      damage: Math.round(damage),
      crit,
      playerHp: Math.round(s.playerHp),
      bossHp: Math.round(s.enemyHp),
      playerShield: Math.round(s.playerShield),
      targetPet: !hitPlayer || undefined,
      ...petFrameFields(s),
    });
  }

  // Mob heal — systematic every 2 turns (after its strike).
  if (!isBoss && allySideAlive(s) && s.enemyHealCooldown === 0 && s.enemyEnergy >= MOB_HEAL_COST) {
    s.enemyEnergy -= MOB_HEAL_COST;
    enemyEnergySpent += MOB_HEAL_COST;
    s.enemyHealCooldown = MOB_HEAL_COOLDOWN;
    s.enemySpellsCast += 1;
    const healed = Math.min((MOB_HEAL_PERCENT_MAX_HP / 100) * s.enemyMaxHp, s.enemyMaxHp - s.enemyHp);
    s.enemyHp += healed;
    s.frames.push({
      turn: s.round * 2,
      attacker: "boss",
      damage: 0,
      crit: false,
      playerHp: Math.round(s.playerHp),
      bossHp: Math.round(s.enemyHp),
      playerShield: Math.round(s.playerShield),
      heal: Math.round(healed),
      spell: ENEMY_HEAL_ID,
      ...petFrameFields(s),
    });
  }

  if (!allySideAlive(s)) {
    s.over = true;
    s.win = false;
    return;
  }

  // ── Vision echo ── the mark punishes the energy the enemy JUST spent:
  // +X% of a base player hit per energy point, at the end of its turn. One
  // turn only — the mark is consumed whether or not it spent anything.
  if (s.visionEchoPerEnergy > 0) {
    if (enemyEnergySpent > 0 && s.enemyHp > 0) {
      const hit = computeHit(s.playerStats, s.enemyStats, 1, rng);
      const echoMult = s.rotationMode ? ROTATION_SPELL_DAMAGE_MULT : 1;
      const echo = hit.damage * (s.visionEchoPerEnergy / 100) * enemyEnergySpent * echoMult;
      damageEnemy(s, echo);
      s.frames.push({
        turn: s.round * 2,
        attacker: "player",
        damage: Math.round(echo),
        crit: hit.crit,
        playerHp: Math.round(s.playerHp),
        bossHp: Math.round(s.enemyHp),
        playerShield: Math.round(s.playerShield),
        spell: "vision",
        ...petFrameFields(s),
      });
    }
    s.visionEchoPerEnergy = 0;
    checkEnemyDown(s);
  }
}

// ── Enemy pet turn ── scripted AoE: ONE strike at 30% of a master base hit on
// EACH living ally (separate rolls). No rage, no heal, no energy bookkeeping.
function enemyPetActs(s: InteractiveCombatState, rng: () => number) {
  if (s.enemyPetHp <= 0) return;
  const reduction = defenseReductionFor(s.playerStats.defense);
  const targets: Array<"player" | "pet"> = [];
  if (s.playerHp > 0) targets.push("player");
  if (s.petHp > 0) targets.push("pet");

  for (const target of targets) {
    const base = s.enemyStats.physique * (0.90 + rng() * 0.20);
    const crit = rng() < critChance(s.enemyStats.technique, s.playerStats.technique);
    const critMult = crit ? CRIT_MULTIPLIER - reduction : 1;
    const raw = base * critMult * s.enemyDamageMult * (ENEMY_PET_DAMAGE_PERCENT / 100);
    const damage = Math.max(1, raw * (1 - reduction));

    if (target === "player") {
      const absorbed = Math.min(s.playerShield, damage);
      s.playerShield -= absorbed;
      s.playerHp = Math.max(0, s.playerHp - (damage - absorbed));
      if (crit && s.shieldExpiresRound != null && s.wallCritGain > 0) {
        s.playerShield += s.wallCritGain;
      }
    } else {
      s.petHp = Math.max(0, s.petHp - damage);
    }

    s.frames.push({
      turn: s.round * 2,
      attacker: "boss",
      actor: "enemyPet",
      damage: Math.round(damage),
      crit,
      playerHp: Math.round(s.playerHp),
      bossHp: Math.round(s.enemyHp),
      playerShield: Math.round(s.playerShield),
      targetPet: target === "pet" || undefined,
      ...petFrameFields(s),
    });
  }

  if (!allySideAlive(s)) {
    s.over = true;
    s.win = false;
  }
}

// ── Ally pet phase opens ── full pet bar; pet cooldowns tick here (once per
// round, mirroring player cooldowns at player turn start).
function enterPetPhase(s: InteractiveCombatState): InteractiveCombatState {
  s.phase = "pet";
  s.petEnergy = PET_ENERGY_PER_TURN;
  for (const key of Object.keys(s.petCooldowns) as PetSpellId[]) {
    const left = (s.petCooldowns[key] ?? 0) - 1;
    if (left <= 0) delete s.petCooldowns[key];
    else s.petCooldowns[key] = left;
  }
  return s;
}

// ── Round rollover ── timeout check, then the next ally phase opens.
function nextRound(s: InteractiveCombatState): InteractiveCombatState {
  // Timeout: both sides alive past the cap → resolve by SIDE HP fraction
  // (pet HP counts; with pets off the pet terms are all 0 — the control rule).
  if (s.round >= MAX_ROUNDS) {
    s.over = true;
    const allyFrac = (s.playerHp + s.petHp) / (s.playerMaxHp + s.petMaxHp);
    const enemyFrac = (s.enemyHp + s.enemyPetHp) / (s.enemyMaxHp + s.enemyPetMaxHp);
    s.win = allyFrac >= enemyFrac;
    return s;
  }

  // ── Next player turn: FULL bar, leftovers don't carry.
  // Rotation mode: the bank carries over — autos are the only energy income.
  s.round += 1;
  if (!s.rotationMode) s.energy = s.energyPerTurn;
  // Turn-scoped effects reset: Focus expires, once-per-turn locks lift.
  s.focusBonusPercent = 0;
  s.oncePerTurnCast = {};
  // Cooldowns tick down at each player turn start.
  for (const key of Object.keys(s.cooldowns) as PveSpellId[]) {
    const left = (s.cooldowns[key] ?? 0) - 1;
    if (left <= 0) delete s.cooldowns[key];
    else s.cooldowns[key] = left;
  }
  // Wall expiry — the timed shield drops at the start of its Nth player turn.
  if (s.shieldExpiresRound != null && s.round >= s.shieldExpiresRound) {
    s.playerShield = 0;
    s.shieldExpiresRound = null;
    s.wallCritGain = 0;
  }
  // 2v2: a dead master hands the round straight to its pet.
  s.phase = "player";
  if (s.petsEnabled && s.playerHp <= 0) enterPetPhase(s);
  return s;
}

// ── Finalization ──────────────────────────────────────────────────────────────

/**
 * Convert a FINISHED fight into the control-variant contract (CombatSequence:
 * frames + BossResult) so savePveStageWin / telemetry / result screens work
 * unchanged. No reward multiplier in variant B (no Greed).
 */
export function finalizeInteractiveCombat(s: InteractiveCombatState): CombatSequence {
  const win = s.win === true;
  const conditionAfter = clamp((s.playerHp / s.playerMaxHp) * MAX_CONDITION, 0, MAX_CONDITION);
  const damageReceived = Math.max(0, Math.round(s.startHp - s.playerHp));
  const rewards = win ? getStageRewards(s.stage) : { xp: 0, gold: 0, chest: false };
  const playerPower = s.playerStats.physique + s.playerStats.technique + s.playerStats.vitesse + s.playerStats.defense;
  const enemyPower = s.enemyStats.physique + s.enemyStats.technique + s.enemyStats.vitesse + s.enemyStats.defense;

  const result: BossResult = {
    win,
    damageReceived,
    conditionAfter,
    xpEarned: rewards.xp,
    goldEarned: rewards.gold,
    chestEarned: rewards.chest,
    // Same rough pre-fight estimate as control (display/telemetry only).
    winChance: calculateMatchAdvantage(playerPower, enemyPower),
  };

  return { frames: s.frames, result, playerMaxHp: s.playerMaxHp, bossMaxHp: s.enemyMaxHp };
}

/** variant_stats payload for pve_fights (jsonb — extend freely, no migration). */
export function interactiveVariantStats(s: InteractiveCombatState): Record<string, unknown> {
  const spellsCastTotal = Object.values(s.spellsCast).reduce((a, b) => a + (b ?? 0), 0);
  const out: Record<string, unknown> = {
    rounds: s.round,
    spells_cast: s.spellsCast,
    spells_cast_total: spellsCastTotal,
    energy_spent: s.energySpent,
    loadout: s.loadout,
    end_energy: s.energy,
    enemy_spells_cast: s.enemySpellsCast,
  };
  if (s.rotationMode) {
    out.rotation_mode = true;
    out.auto_attacks = s.autoAttacks;
    out.rotation_cycles = s.rotationIndex;
  }
  if (s.petsEnabled) {
    out.pets = {
      pet_spells_cast: s.petSpellsCast,
      pet_energy_spent: s.petEnergySpent,
      pet_hp_end: Math.round(s.petHp),
      pet_max_hp: s.petMaxHp,
      enemy_pet_hp_end: Math.round(s.enemyPetHp),
      enemy_pet_max_hp: s.enemyPetMaxHp,
      master_died: s.playerHp <= 0,
      pet_died: s.petHp <= 0,
    };
  }
  return out;
}
